import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/utils/toast.utils';
import {
  createVehiculeCheckResult,
  getRepairData,
  getResultByVehiculeCheckId,
  updateResults,
  updateVehiculeCheckResult,
  uploadVehiculeCheckResultImage,
  validateReport,
  getAllRepair,
  deleteManyVehiculeCheckResultsByVehiculeCheckId,
  customizeResultImage,
} from '@/api/vehicule-check-result/vehicule-check-result';
import { useGetExistingResourceUrl } from './resource-url.hooks';
import { TCustomQueryProps } from '@/type/hooks.types';
import { NOTIFY, R2_PUBLIC_DOMAIN } from '@/utils/constant.utils';

import { ErrorResponse } from '@/utils/axios.http';
import { CustomizeResultImage } from '@/api/entities/vehicule-check-result';

export const useGetResultByVehiculeCheckId = (vehiculeCheckId: string) => {
  return useQuery({
    queryKey: ['getResultByVehiculeCheckId', vehiculeCheckId],
    queryFn: () => getResultByVehiculeCheckId(vehiculeCheckId),
    // enabled: !!vehiculeCheckId,
  });
};

export const useGetVehiculeCheckVideo = (id: string) => {
  const videoUrl = `${R2_PUBLIC_DOMAIN}/videos/${id}.mp4`;

  const { getExistingUrl } = useGetExistingResourceUrl();

  const { data: cachedVideoUrl, isLoading: isLoadingQuery } = useQuery({
    queryKey: ['vehiculeCheckVideo', videoUrl],
    queryFn: async () => {
      const result = await getExistingUrl(videoUrl);
      return result || null; // Fallback to original videoUrl if resource check fails
    },
    staleTime: Infinity,
    enabled: !!id && !!videoUrl,
  });

  return {
    uncachedVideoUrl: videoUrl,
    videoUrl: cachedVideoUrl,
    isLoading: isLoadingQuery,
  };
};

export const useUpdateVehiculeCheckResult = ({
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: object }) =>
      updateVehiculeCheckResult(id, data),
    onSuccess: (data) => {
      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      console.error(error.error);
    },
  });
};

export const useCreateVehiculeCheckResult = ({
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  return useMutation({
    mutationFn: (data: object) => createVehiculeCheckResult(data),
    onSuccess: (data) => {
      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      console.error(error.error);
    },
  });
};

export const useUploadVehiculeCheckResultImage = ({
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  return useMutation({
    mutationFn: (formData: FormData) =>
      uploadVehiculeCheckResultImage(formData),
    onSuccess: (data) => {
      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      console.error(error.error);
    },
  });
};

export const useUpdateResults = ({
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      results,
      canModerate,
      moderationStatus,
    }: {
      id: string;
      results: object[];
      canModerate: boolean;
      moderationStatus: string;
    }) => updateResults(id, results, canModerate, moderationStatus),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['getResultByVehiculeCheckId'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getAllRepair'],
      });

      handleSuccess?.(variables);
    },
    onError: (error: ErrorResponse) => {
      console.error('Error when updating results:', error);
    },
  });
};

export const useCustomizeResultImage = ({
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CustomizeResultImage) => customizeResultImage(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['getResultByVehiculeCheckId'],
      });

      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      console.error('Error when customizing result image:', error);
    },
  });
};

export const useGetRepairsData = (vehiculeCheckId: string) => {
  return useQuery({
    queryKey: ['getRepairData', vehiculeCheckId],
    queryFn: () => getRepairData(vehiculeCheckId),
    enabled: !!vehiculeCheckId,
  });
};

export const useGetValidateReport = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => validateReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getResultByVehiculeCheckId'],
      });
      notify(t('reportValidation'), NOTIFY.SUCCESS);
      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when validating report:', error);
    },
  });
};

export const useGetAllRepair = () => {
  return useQuery({
    queryKey: ['getAllRepair'],
    queryFn: () => getAllRepair(),
    staleTime: Infinity,
  });
};

export const useDeleteManyVehiculeCheckResultsByVehiculeCheckId = ({
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vehiculeCheckId: string) =>
      deleteManyVehiculeCheckResultsByVehiculeCheckId(vehiculeCheckId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getResultByVehiculeCheckId'],
      });

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      console.error(error.error);
    },
  });
};
